import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useParams } from 'react-router-dom';

import { KanbanMain } from '@/components/layouts/kanban';
import {
  TOOLTIP_PORTAL,
  TRAY_PORTAL_LEFT,
  TRAY_PORTAL_RIGHT
} from '@/constant/htmlIds';
import { FIRST } from '@/constant/pagination';
import { ROUTE_WORKFLOW_DASHBOARD } from '@/constant/routes';
import {
  FAST_WORKFLOW_COUNT_QUERY,
  FAST_WORKFLOW_QUERY
} from '@/gql/queries/fast-workflow-overview.gql';
import { useErrorContext } from '@/hooks/useErrorContext';
import { useUser } from '@/hooks/useUser';
import { deepCopy, deepGet } from '@/utils';
import {
  useWorkflowOverviewContext,
  WorkflowOverviewContextProvider
} from '../workflowOverview/context.workflowOverview';
import {
  findGroupTraceStatesByIndex,
  getFiltersFromUserDisplayConfig,
  getGroupFromUserDisplayConfig,
  getSortFromUserDisplayConfig
} from '../workflowOverview/utils';
import { WorkflowOverviewActions } from '../workflowOverview/workflowOverviewActions';
import { WorkflowOverviewTable } from '../workflowOverview/workflowOverviewTable';
// we keep back import for now
import { FastWorkflowOverviewHeader } from './fastWorkflowOverviewHeader';

const FastWorkflowOverviewComp = memo(() => {
  const { user, loading: userLoading } = useUser();
  const { handleError } = useErrorContext();
  const { id: workflowId } = useParams<{ id: string }>();
  const context = useWorkflowOverviewContext();
  const { archiveDisplayTraces, userDisplayConfig } = context;

  const filters = useMemo(
    () => getFiltersFromUserDisplayConfig(userDisplayConfig),
    [userDisplayConfig]
  );
  const sort = useMemo(
    () => getSortFromUserDisplayConfig(userDisplayConfig),
    [userDisplayConfig]
  );
  const groupBy = useMemo(
    () => getGroupFromUserDisplayConfig(userDisplayConfig),
    [userDisplayConfig]
  );
  const archived = archiveDisplayTraces;

  Sentry.setContext('Fast Workflow Overview', {
    workflowId,
    filters,
    sort,
    groupBy,
    user,
    userDisplayConfig,
    workflowOverviewContext: context
  });

  useEffect(() => {
    localStorage.setItem('fastWorkflowOverview', 'true');
  }, []);

  const {
    loading: workflowLoading,
    error,
    data,
    previousData,
    fetchMore,
    refetch
  } = useQuery(FAST_WORKFLOW_QUERY, {
    variables: {
      workflowId,
      first: FIRST,
      filter: filters,
      orderBy: sort,
      archived
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
    onError: error => {
      console.error('Error fetching workflow data');
      Sentry.captureException(error);
    }
  });

  const { data: countData } = useQuery(FAST_WORKFLOW_COUNT_QUERY, {
    variables: {
      workflowId,
      filter: filters,
      archived
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: error => {
      console.error('Error fetching count data', error);
      Sentry.captureException(error);
    }
  });

  const workflow = useMemo(() => {
    let wf = data?.workflowByRowId ?? previousData?.workflowByRowId;
    if (!wf) return;

    wf = deepCopy(wf);
    const traceState = wf.fastTraceStates as typeof wf.fastTraceStates & {
      totalCount: number;
    };

    wf['groupedTraceStates'] = {
      nodes: [
        {
          index: null,
          traceStates: traceState,
          __typename: 'GroupedTraceState'
        }
      ]
    };

    // update the count of traces, this is not the cleanest option but it's the easiest to implement for now
    if (countData?.workflowCount?.fastTraceStatesCount) {
      const count = countData.workflowCount.fastTraceStatesCount;
      wf['groupedTraceStates']['nodes'][0]['traceStates']['totalCount'] = count;
    }

    return wf;
  }, [data, previousData, countData]);

  const fetchMorePromise = useRef<Promise<void> | null>(null);

  // Is loading if the user info or the workflow info is missing,
  // and the gql call is loading.
  // The goal here is to render the old data when we are refetching

  const loading = !(workflow && user) && (workflowLoading || userLoading);

  const selectedGroupIndex = deepGet(
    userDisplayConfig,
    'groupBy.selected'
  ) as Maybe<number>;
  // Raise 404 page when workflow not found.
  useEffect(() => {
    // This check avoids rerendering the component after the erroBoundary has been triggered
    if (!(workflowLoading || workflow) || error) {
      handleError('workflow', workflowId, ROUTE_WORKFLOW_DASHBOARD, error);
    }
  }, [error, workflowLoading, workflow, workflowId, handleError]);

  useEffect(() => {
    const { name } = workflow || {};
    if (name) document.title = `${name} - Overview - Trace`;
  }, [workflow]);

  const handleOnLoadMore = useCallback(() => {
    if (!workflow) return;
    // If a fetch more query is in progress, do not try to load
    if (fetchMorePromise.current) return;
    // If grouping is in effect and all groups are collapsed, do not try to load
    // TODO: Handle group pagination; right now, only traces within the expanded group are paginated
    const groupBy = getGroupFromUserDisplayConfig(userDisplayConfig);
    if (groupBy && selectedGroupIndex === undefined) return;
    const { rowId: workflowRowId } = workflow;
    const groupedTraceStates = {
      nodes: [
        {
          index: null,
          traceStates: workflow.fastTraceStates
        }
      ]
    };
    // fetchMore will only fetch more traces for one group
    // (if groupBy is null, all the additional traces will be available in one group with index 'null')
    const traceStates = findGroupTraceStatesByIndex(
      groupedTraceStates,
      selectedGroupIndex
    );
    const { endCursor } = traceStates.pageInfo;
    fetchMorePromise.current = fetchMore({
      variables: {
        workflowId: workflowRowId,
        first: FIRST,
        cursor: endCursor,
        filter: getFiltersFromUserDisplayConfig(userDisplayConfig),
        orderBy: getSortFromUserDisplayConfig(userDisplayConfig),
        archived
      }
    })
      .then(() => {
        fetchMorePromise.current = null;
      })
      .catch(error => {
        console.error('Error fetching more traces', error);
        Sentry.captureException(error);
        fetchMorePromise.current = null;
      });
  }, [workflow, userDisplayConfig, archived]);

  const main = useMemo(
    () => (
      <>
        <WorkflowOverviewTable
          handleOnLoadMore={handleOnLoadMore}
          refetchTraces={refetch}
          user={user}
          workflow={workflow}
        />
        <WorkflowOverviewActions
          selectedGroupIndex={selectedGroupIndex}
          workflow={workflow}
        />
      </>
    ),
    [workflow, handleOnLoadMore, refetch, selectedGroupIndex, user]
  );

  // If the workflow id doesn't exist,
  // we return null and let errorBoundary render the error page
  if (error || !(workflow || loading)) {
    return null;
  }

  return (
    <>
      <div id={TOOLTIP_PORTAL} />
      <div
        className="fixed top-0 bottom-0 left-0 z-5 flex w-auto flex-row flex-nowrap"
        id={TRAY_PORTAL_LEFT}
      />
      <div
        className="fixed top-0 right-0 bottom-0 z-5 flex w-auto flex-row-reverse flex-nowrap"
        id={TRAY_PORTAL_RIGHT}
      />

      <FastWorkflowOverviewHeader isLoading={loading} workflow={workflow} />
      <KanbanMain data-testid="layout-kanban-main" loading={loading}>
        {!loading && main}
      </KanbanMain>
    </>
  );
});

export const FastWorkflowOverviewWithContext = memo(() => (
  <WorkflowOverviewContextProvider>
    <FastWorkflowOverviewComp />
  </WorkflowOverviewContextProvider>
));
